import { Flex, Layout } from "antd";
import { FullLoader } from "components/FullLoader";
import {
	ReportIssueModal,
	type ReportKind,
} from "components/ReportIssue/ReportIssueModal";
import { useUser } from "hooks/useUser";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { applyPrivacyMask, clearPrivacyMask } from "utils/privacyMask";
import { AppFooter } from "./AppFooter";
import { AppHeader } from "./AppHeader";
import { AppSider } from "./AppSider";
import {
	CustomerSupportAccessBar,
	SupportAccessConsentModal,
} from "./CustomerSupportAccess";
import { Events } from "./Events";
import { SidebarProvider } from "./SidebarContext";
import { SupportAccessBar } from "./SupportAccessBar";
import { SupportAccessFrame } from "./SupportAccessFrame";

export function AppLayout() {
	const { user, isLoading } = useUser();
	// Owned here, not in AppHeader or the modal: those two are siblings in the
	// tree, so neither can hold state the other needs. Plain prop-drilling is
	// enough for two consumers — and this must NOT go in auth.store, which
	// persists to session storage and would reopen the modal on a later visit.
	const [reportKind, setReportKind] = useState<ReportKind | null>(null);

	// Mask the document for Hotjar/Heap/PostHog for as long as the signed-in
	// shell is mounted. Above the `isLoading` return on purpose: this is the one
	// component every screen that renders a client's books sits inside, and the
	// mask has to be in place before the first row paints, not after the user
	// resolves. See utils/privacyMask.ts for why it targets document.body.
	useEffect(() => {
		applyPrivacyMask();
		return clearPrivacyMask;
	}, []);

	if (isLoading || !user) return <FullLoader />;

	return (
		<SidebarProvider>
			{/*
			 * The OUTER Layout is vertical, and that is the whole reason it exists.
			 * AntD switches a Layout to `flex-direction: row` when one of its
			 * DIRECT children is a Sider — so putting the session bar next to
			 * <AppSider /> renders it as a vertical strip down the side of the
			 * page rather than a bar across the top. Wrapping the row in a
			 * vertical parent is the only arrangement that puts the bar above
			 * both the sider and the content.
			 */}
			<Layout style={layoutStyle}>
				<SupportAccessBar />
				<CustomerSupportAccessBar />
				<Layout hasSider>
					<a href="#main-content" style={skipLinkStyle}>
						Skip to main content
					</a>
					<Events />
					<AppSider />
					<Layout>
						<Flex vertical style={contantStyle}>
							<AppHeader onReportIssue={setReportKind} />
							<main id="main-content" style={{ flex: 1 }}>
								<Outlet />
							</main>
							<AppFooter />
						</Flex>
					</Layout>
				</Layout>
			</Layout>
			<SupportAccessFrame />
			<SupportAccessConsentModal />
			{/* Mounted once, outside <Outlet />, so a draft survives navigation.
			    The route being reported is frozen when the modal opens. */}
			<ReportIssueModal
				open={reportKind !== null}
				initialKind={reportKind ?? "bug"}
				onClose={() => setReportKind(null)}
			/>
		</SidebarProvider>
	);
}

const layoutStyle: React.CSSProperties = {
	minHeight: "100vh",
};

const contantStyle: React.CSSProperties = {
	position: "relative",
	// The session bar is in normal flow above this, so the scroll pane has to
	// give back exactly its height or the page grows past the viewport. The
	// variable is set by SupportAccessBar and defaults to 0px, which makes the
	// ordinary no-session case identical to before.
	height: "calc(100vh - var(--support-access-bar-height, 0px))",
	overflowY: "auto",
	// Reserve the scrollbar's width whether or not it is shown, so toggling
	// the vertical scrollbar at the content-height boundary cannot oscillate
	// the available width. Without this, an AntD Tabs nav sitting right at the
	// boundary (e.g. company Settings) flickers by the scrollbar width every
	// frame and drives rc-tabs' ResizeObserver into an infinite setState loop
	// ("Maximum update depth exceeded").
	scrollbarGutter: "stable",
	background: "white",
};

const skipLinkStyle: React.CSSProperties = {
	position: "absolute",
	top: -40,
	left: 0,
	background: "var(--color-primary)",
	color: "white",
	padding: "8px 16px",
	zIndex: 100,
	transition: "top 200ms ease",
};
// Skip link becomes visible on focus via CSS in index.css
